import { Stack, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useGetCampaignFieldsQuery } from "../../../../../api/referencesApi";
import { DropdownNestedItem } from "../../../../../types/genereal";
import { CustomButton } from "../../../../ui/CustomButton";
import { NestedDropdown } from "./NestedDropdown";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { CustomToast } from "../../../../ui/CustomToast";
import { toast } from "react-toastify";
import {
  AccountSize,
  CampaignSavedData,
  TargetIndustry,
  TargetRegion,
} from "../../../../../types/apiTypes";
import {
  toDropdownIndustries,
  toDropdownRegions,
} from "../../../../../utils/convertToDropdownNesteItem";
import { SizesDropdown } from "./SizesDropdown";
import { useGenerateJobTitles } from "../../../../../hooks";

type LabelWrapperProps = {
  children: ReactNode;
  title: string;
  label: string;
};

const LabelWrapper = ({ children, label, title }: LabelWrapperProps) => (
  <Stack gap="8px">
    <Stack>
      <Typography variant="subtitle1" fontWeight="500">
        {title}
      </Typography>

      <Typography variant="body1" color="text.secondary">
        {label}
      </Typography>
    </Stack>

    {children}
  </Stack>
);

type Props = {
  setResultData: React.Dispatch<React.SetStateAction<CampaignSavedData>>;
  initData: CampaignSavedData;
  nextStepHandler: () => void;
  backHandler: () => void;
};

export const AddPreferencesStep = ({
  backHandler,
  nextStepHandler,
  setResultData,
  initData,
}: Props) => {
  const { data, isError, error } = useGetCampaignFieldsQuery();

  const { target_industries, target_regions, target_account_sizes } = initData;

  const initialIndustries = toDropdownIndustries(target_industries || []);
  const [selectedIndustries, setSelectedIndustries] =
    useState<DropdownNestedItem[]>(initialIndustries);

  const initialRegions = toDropdownRegions(target_regions || []);
  const [selectedRegions, setSelectedRegions] =
    useState<DropdownNestedItem[]>(initialRegions);

  const [selectedSizes, setSelectedSizes] = useState<AccountSize[]>(
    target_account_sizes || [],
  );

  useEffect(() => {
    saveData();
  }, [selectedRegions, selectedIndustries, selectedSizes]);

  useEffect(() => {
    if (isError && error) {
      toast.error(
        <CustomToast
          type="error"
          text={
            getErrorMessage(error).globalError ||
            "Failed to fetch campaign fields"
          }
          title="Error"
        />,
      );
    }
  }, [isError]);

  const campaignFields = data || {
    account_sizes: [],
    regions: [],
    industries: [],
  };

  const industries = toDropdownIndustries(campaignFields.industries);
  const regions = toDropdownRegions(campaignFields.regions);

  const saveData = () => {
    const transformedIndustries: TargetIndustry[] = selectedIndustries.map(
      (item) => ({
        name: item.name,
        sub_industries: item.selected ? [] : item.nestedItems,
        is_all_sub_industries: item.selected,
      }),
    );

    const transformedRegions: TargetRegion[] = selectedRegions.map((item) => ({
      name: item.name,
      countries: item.selected ? [] : item.nestedItems,
      is_all_countries: item.selected,
    }));

    setResultData((prev) => ({
      ...prev,
      target_industries: transformedIndustries,
      target_regions: transformedRegions,
      target_account_sizes: selectedSizes,
    }));
  };

  const { generateJobTitles, isLoadingTitles } = useGenerateJobTitles(initData);

  const summitHandler = async () => {
    const titles = await generateJobTitles();
    setResultData((prev) => ({ ...prev, target_audiences: titles }));
    nextStepHandler();
  };

  return (
    <Stack direction="column" gap="24px" width="100%">
      <LabelWrapper
        title="Geo"
        label="Select the region or specific countries where your target accounts are located."
      >
        <NestedDropdown
          items={regions}
          selected={selectedRegions}
          setSelected={setSelectedRegions}
          placeholder="Select Region or Countries"
          selectAllText="Whole region"
        />
      </LabelWrapper>

      <LabelWrapper
        title="Industry"
        label="Select industries or sub-industries that match your ideal customer profile."
      >
        <NestedDropdown
          items={industries}
          selected={selectedIndustries}
          setSelected={setSelectedIndustries}
          placeholder="Select Industry or Sub-Industry"
          selectAllText="Whole industry"
        />
      </LabelWrapper>

      <LabelWrapper
        title="Size"
        label="Select the company size by employees count that best fits your target accounts."
      >
        <SizesDropdown
          items={campaignFields.account_sizes}
          selected={selectedSizes}
          setSelected={setSelectedSizes}
        />
      </LabelWrapper>

      <Stack direction="row" gap="8px">
        <CustomButton
          variant="contained"
          textColor="#4C545B"
          fullWidth
          type="button"
          customColor="#F1F1F1"
          onClick={backHandler}
        >
          Back
        </CustomButton>
        <CustomButton
          onClick={summitHandler}
          variant="contained"
          fullWidth
          type="button"
          customColor="#1C3C6C"
          loading={isLoadingTitles}
          disabled={
            !selectedIndustries.length ||
            !selectedRegions.length ||
            !selectedSizes.length
          }
        >
          Next Step
        </CustomButton>
      </Stack>
    </Stack>
  );
};
